/**
 * MultiSelect — sélection multi-options avec tags amovibles.
 *
 * Primitive générique réutilisable :
 *  - GuaranteeMultiSelect (avec groupes)
 *  - sélecteur d'assureurs
 *  - sélecteur de tags catalogue (futur)
 *
 * Volontairement minimaliste — pas de combobox autocomplete, pas de virtualisation.
 * Pour notre catalogue (≤12 assureurs, ~25 garanties), un simple <select> avec
 * affichage des choix retenus en chips suffit largement.
 *
 * A11y :
 *  - <select> natif (clavier OK partout)
 *  - chips avec bouton "remove" focusable
 */

import { useTranslation } from "react-i18next";

import { cn } from "../lib/cn";

export type MultiSelectOption<T extends string = string> = {
  value: T;
  label: string;
  /** Optionnel : groupe (ex. "vie", "amc"). Active <optgroup>. */
  group?: string;
  /** Optionnel : label du groupe (sinon `group` est utilisé tel quel). */
  groupLabel?: string;
};

type Props<T extends string> = {
  options: readonly MultiSelectOption<T>[];
  /** Valeurs sélectionnées. */
  value: readonly T[];
  onChange: (next: T[]) => void;
  placeholder?: string;
  /** ID DOM pour le label parent (a11y). */
  id?: string;
  /** Désactive l'ajout de nouvelles entrées (mais les chips restent retirables). */
  disabled?: boolean;
};

export function MultiSelect<T extends string>({
  options,
  value,
  onChange,
  placeholder,
  id,
  disabled,
}: Props<T>) {
  const { t } = useTranslation();
  const placeholderText = placeholder ?? t("common.select_placeholder");

  // Regroupe par optgroup si présent.
  const grouped = new Map<string | undefined, MultiSelectOption<T>[]>();
  for (const opt of options) {
    const k = opt.group;
    if (!grouped.has(k)) grouped.set(k, []);
    grouped.get(k)!.push(opt);
  }

  const labelByValue = new Map(options.map((o) => [o.value, o.label]));

  const remaining = options.filter((o) => !value.includes(o.value));

  const onAdd = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value as T;
    if (v === "") return;
    if (!value.includes(v)) onChange([...value, v]);
    e.target.value = ""; // reset visuel
  };

  const onRemove = (v: T) => {
    onChange(value.filter((x) => x !== v));
  };

  return (
    <div>
      <select
        id={id}
        className="input"
        onChange={onAdd}
        defaultValue=""
        disabled={disabled || remaining.length === 0}
        aria-label={placeholderText}
      >
        <option value="" disabled>
          {remaining.length === 0 ? t("common.all_selected") : placeholderText}
        </option>
        {[...grouped.entries()].map(([groupKey, opts]) => {
          const visible = opts.filter((o) => !value.includes(o.value));
          if (visible.length === 0) return null;
          if (groupKey === undefined) {
            return visible.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ));
          }
          const groupLabel = visible[0]?.groupLabel ?? groupKey;
          return (
            <optgroup key={groupKey} label={groupLabel}>
              {visible.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </optgroup>
          );
        })}
      </select>

      {value.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {value.map((v) => (
            <li
              key={v}
              className={cn(
                "inline-flex items-center gap-1 rounded-full bg-brand-100 px-2 py-0.5 text-xs text-brand-700",
              )}
            >
              <span>{labelByValue.get(v) ?? v}</span>
              <button
                type="button"
                onClick={() => onRemove(v)}
                disabled={disabled}
                aria-label={t("common.remove", { item: labelByValue.get(v) ?? v })}
                className="text-brand-500 hover:text-brand-800 disabled:text-slate-300"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
